import { useEffect, useState } from "react";
import { FiUser, FiShield, FiBell, FiCreditCard } from "react-icons/fi";
import Swal from "sweetalert2";
import api from "../../Services/Api";
import { getUserById, updateUserById } from "../../Services/UserService";

const initialFormState = {
  userId: "",
  userName: "",
  email: "",
  phoneNumber: "",
  address: "",
  alternativePhoneNumber: "",
  alternativeEmail: "",
  dateOfBirth: "",
  gender: "",
  occupation: "",
  nationality: "",
  profileImageUrl: "",
};

const formatDateForInput = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().split("T")[0];
};

const mapUserResponseToForm = (user) => ({
  userId: user.userId ?? "",
  userName: user.userName ?? "",
  email: user.email ?? "",
  phoneNumber: user.phoneNumber ?? "",
  address: user.address ?? "",
  alternativePhoneNumber: user.alternativePhoneNumber ?? "",
  alternativeEmail: user.alternativeEmail ?? "",
  dateOfBirth: formatDateForInput(user.dateOfBirth),
  gender: user.gender ?? "",
  occupation: user.occupation ?? "",
  nationality: user.nationality ?? "",
  profileImageUrl: user.profileImageUrl ?? "",
});

const buildUpdatePayload = (formData) => ({
  userName: formData.userName.trim(),
  email: formData.email.trim(),
  phoneNumber: formData.phoneNumber.trim(),
  address: formData.address.trim(),
  alternativePhoneNumber: formData.alternativePhoneNumber.trim() || null,
  alternativeEmail: formData.alternativeEmail.trim() || null,
  dateOfBirth: formData.dateOfBirth || null,
  gender: formData.gender.trim() || null,
  occupation: formData.occupation.trim() || null,
  nationality: formData.nationality.trim() || null,
  profileImageUrl: formData.profileImageUrl.trim() || null,
});

const getResolvedProfileImageUrl = (profileImageUrl) => {
  if (!profileImageUrl) {
    return "";
  }

  try {
    return new URL(profileImageUrl, api.defaults.baseURL).toString();
  } catch (error) {
    return profileImageUrl;
  }
};

const getStoredUser = () => {
  const storedUser = localStorage.getItem("user");

  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch (error) {
    localStorage.removeItem("user");
    return null;
  }
};

export default function AccountSettings() {
  const [activeSection, setActiveSection] = useState("personal");
  const [formData, setFormData] = useState(initialFormState);
  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  const [removeProfileImage, setRemoveProfileImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const userId = getStoredUser()?.userId ?? "";

    if (!userId) {
      setError("Please log in to view your account settings.");
      setLoading(false);
      return;
    }

    const loadUser = async () => {
      try {
        const response = await getUserById(userId);
        setFormData(mapUserResponseToForm(response.data));
        setSelectedProfileImage(null);
        setRemoveProfileImage(false);
        setError("");
      } catch (requestError) {
        setError(
          requestError.response?.data?.error ||
            "Unable to load account settings right now."
        );
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const sections = [
    {
      key: "personal",
      title: "Personal Information",
      sidebarLabel: "Personal Info",
      icon: FiUser,
      content: (
        <PersonalInfo
          formData={formData}
          loading={loading}
          saving={saving}
          error={error}
          onChange={setFormData}
          selectedProfileImage={selectedProfileImage}
          removeProfileImage={removeProfileImage}
          onProfileImageChange={setSelectedProfileImage}
          onRemoveProfileImageChange={setRemoveProfileImage}
          onSave={async () => {
            if (!formData.userId) {
              setError("User ID is missing. Please log in again.");
              return;
            }

            setSaving(true);
            setError("");

            try {
              const payload = {
                ...buildUpdatePayload(formData),
                profileImage: selectedProfileImage,
                removeProfileImage,
                profileImageUrl: removeProfileImage
                  ? null
                  : formData.profileImageUrl.trim() || null,
              };
              const response = await updateUserById(formData.userId, payload);
              const updatedUser = mapUserResponseToForm(response.data);

              setFormData(updatedUser);
              setSelectedProfileImage(null);
              setRemoveProfileImage(false);

              const parsedUser = getStoredUser() ?? {};
              localStorage.setItem(
                "user",
                JSON.stringify({
                  ...parsedUser,
                  userId: updatedUser.userId,
                  name: updatedUser.userName,
                  email: updatedUser.email,
                  profileImageUrl: updatedUser.profileImageUrl,
                })
              );
              window.dispatchEvent(new Event("user-updated"));

              await Swal.fire({
                icon: "success",
                title: "Profile updated",
                text: "Your account details were saved successfully.",
                timer: 1500,
                showConfirmButton: false,
              });
            } catch (requestError) {
              const message =
                requestError.response?.data?.error ||
                requestError.response?.data?.message ||
                "Unable to save your changes.";
              setError(message);
              await Swal.fire({
                icon: "error",
                title: "Update failed",
                text: message,
              });
            } finally {
              setSaving(false);
            }
          }}
        />
      ),
    },
    {
      key: "privacy",
      title: "Privacy",
      sidebarLabel: "Privacy",
      icon: FiShield,
      content: (
        <div className="text-neutral-textSecondary hidden">
          Privacy settings coming soon...
        </div>
      ),
    },
    {
      key: "notifications",
      title: "Notifications",
      sidebarLabel: "Notifications",
      icon: FiBell,
      content: (
        <div className="text-neutral-textSecondary hidden">
          Notification settings coming soon...
        </div>
      ),
    },
    {
      key: "financial",
      title: "Financial",
      sidebarLabel: "Financial",
      icon: FiCreditCard,
      content: (
        <div className="text-neutral-textSecondary hidden">
          Financial settings coming soon...
        </div>
      ),
    },
  ];

  const toggleSection = (section) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  return (
    <div className="min-h-screen bg-body flex">
      <div className="w-60 text-neutral-textSecondary p-6 hidden md:block">
        <h2 className="text-lg font-bold mb-6 text-neutral-textPrimary">My Account</h2>

        <nav className="space-y-3 text-sm">
          {sections.map(({ key, sidebarLabel, icon: Icon }) => (
            <SidebarItem
              key={key}
              icon={<Icon />}
              label={sidebarLabel}
              active={activeSection === key}
              onClick={() => setActiveSection(key)}
            />
          ))}
        </nav>
      </div>

      <div className="flex-1 p-6 md:p-10">
        <div className="space-y-4">
          {sections.map(({ key, title, content }) => (
            <AccordionItem
              key={key}
              title={title}
              isOpen={activeSection === key}
              onClick={() => toggleSection(key)}
            >
              {content}
            </AccordionItem>
          ))}
        </div>
      </div>
    </div>
  );
}

function SidebarItem({ icon, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition ${
        active
          ? "bg-accent-gold text-primary-dark font-semibold"
          : "hover:bg-white/10"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function AccordionItem({ title, children, isOpen, onClick }) {
  return (
    <div className="border border-neutral-border rounded-xl overflow-hidden bg-white shadow-sm">
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex justify-between items-center px-5 py-4 text-left font-medium transition ${
          isOpen
            ? "bg-accent-gold text-primary-dark"
            : "bg-neutral-light text-primary-dark hover:bg-primary-default/5"
        }`}
      >
        {title}
        <span className="text-lg">{isOpen ? "-" : "+"}</span>
      </button>

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[1400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function PersonalInfo({
  formData,
  loading,
  saving,
  error,
  onChange,
  selectedProfileImage,
  removeProfileImage,
  onProfileImageChange,
  onRemoveProfileImageChange,
  onSave,
}) {
  const handleChange = (event) => {
    const { name, value } = event.target;
    onChange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    onProfileImageChange(file);

    if (file) {
      onRemoveProfileImageChange(false);
    }
  };

  const handleRemoveProfileImageChange = (event) => {
    const shouldRemove = event.target.checked;
    onRemoveProfileImageChange(shouldRemove);

    if (shouldRemove) {
      onProfileImageChange(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-border bg-neutral-light px-4 py-6 text-sm text-neutral-textSecondary">
        Loading account details...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="User ID"
          name="userId"
          value={formData.userId}
          disabled
        />
        <Input
          label="Full Name*"
          name="userName"
          value={formData.userName}
          onChange={handleChange}
          placeholder="Enter full name"
          disabled
        />
        <Input
          label="Phone Number*"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleChange}
          placeholder="+91 9999999999"
          disabled
        />
        <Input
          label="Email*"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="example@email.com"
          disabled
        />
        <Input
          label="Address*"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="India"
        />
        <Input
          label="Phone Number 2"
          name="alternativePhoneNumber"
          value={formData.alternativePhoneNumber}
          onChange={handleChange}
          placeholder="+91 9876543210"
        />
        <Input
          label="Email 2"
          name="alternativeEmail"
          value={formData.alternativeEmail}
          onChange={handleChange}
          placeholder="backup@email.com"
        />
        <Input
          label="Date of Birth"
          type="date"
          name="dateOfBirth"
          value={formData.dateOfBirth}
          onChange={handleChange}
        />
        <SelectInput
          label="Gender"
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          options={["Male", "Female", "Other"]}
        />
        <Input
          label="Occupation"
          name="occupation"
          value={formData.occupation}
          onChange={handleChange}
          placeholder="Software Engineer"
        />
        <Input
          label="Nationality"
          name="nationality"
          value={formData.nationality}
          onChange={handleChange}
          placeholder="Indian"
        />
        <div className="md:col-span-2 space-y-3">
          <div>
            <label className="text-sm font-medium text-primary-dark">
              Profile Image
            </label>
            <input
              key={
                selectedProfileImage?.name ??
                (removeProfileImage ? "remove" : "empty")
              }
              type="file"
              accept="image/*"
              onChange={handleProfileImageChange}
              className="mt-1 w-full rounded-lg border border-neutral-border px-4 py-2 outline-none file:mr-4 file:rounded-md file:border-0 file:bg-primary-default file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-primary-light"
            />
            <p className="mt-2 text-xs text-neutral-textSecondary">
              {selectedProfileImage
                ? `Selected file: ${selectedProfileImage.name}`
                : "Upload a JPG, PNG, GIF, BMP, or WEBP image."}
            </p>
          </div>

          {formData.profileImageUrl && !removeProfileImage && (
            <div className="rounded-lg border border-neutral-border bg-neutral-light p-4">
              <p className="text-sm font-medium text-primary-dark">
                Current Profile Image
              </p>
              <img
                src={getResolvedProfileImageUrl(formData.profileImageUrl)}
                alt="Current profile"
                className="mt-3 h-24 w-24 rounded-full border border-neutral-border object-cover"
              />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-neutral-textSecondary">
            <input
              type="checkbox"
              checked={removeProfileImage}
              onChange={handleRemoveProfileImageChange}
              className="h-4 w-4 rounded border-neutral-border text-primary-default focus:ring-primary-light"
            />
            Remove current profile image
          </label>
        </div>
      </div>

      <div className="text-right">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="bg-primary-default text-white px-6 py-2 rounded-lg shadow hover:bg-primary-light transition disabled:cursor-not-allowed disabled:opacity-70"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function Input({ label, type = "text", placeholder, ...rest }) {
  return (
    <div>
      <label className="text-sm font-medium text-primary-dark">{label}</label>

      <input
        type={type}
        placeholder={placeholder}
        className="mt-1 w-full rounded-lg border border-neutral-border px-4 py-2 outline-none focus:ring-2 focus:ring-primary-light disabled:bg-neutral-light disabled:text-neutral-textSecondary"
        {...rest}
      />
    </div>
  );
}

function SelectInput({ label, options, ...rest }) {
  return (
    <div>
      <label className="text-sm font-medium text-primary-dark">{label}</label>
      <select
        className="mt-1 w-full rounded-lg border border-neutral-border px-4 py-2 outline-none focus:ring-2 focus:ring-primary-light"
        {...rest}
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}
